// ABOUTME: Module-level queue of captured analytics events, flushed in batches to the server.
// ABOUTME: Flushes on size, on a timer and on tab hide (sendBeacon); capture never throws into the app.

import type { AnalyticsBatch, AnalyticsEvent } from '../types';
import { getAnalyticsSessionId } from './sessionId';

export const ANALYTICS_ENDPOINT = '/api/user-analytics/events';
export const FLUSH_INTERVAL_MS = 10_000;
export const FLUSH_SIZE = 20;
export const MAX_QUEUE_SIZE = 200;
const MAX_BATCH_SIZE = 50; // server cap per request

let enabled = false;
let queue: AnalyticsEvent[] = [];
let timer: ReturnType<typeof setInterval> | null = null;
let currentRoute: { routePattern: string | null; path: string | null } = {
    routePattern: null,
    path: null,
};
// Survives the remounts caused by Routes rebuilding its router, so one navigation = one page view.
let lastPageViewPath: string | null = null;

// Metadata is read before the events are taken out, so a failure leaves the queue intact.
function takeBatch(): AnalyticsBatch | null {
    if (queue.length === 0) return null;
    const sessionId = getAnalyticsSessionId();
    const screenWidth = window.screen?.width ?? null;
    const screenHeight = window.screen?.height ?? null;
    return { sessionId, screenWidth, screenHeight, events: queue.splice(0, MAX_BATCH_SIZE) };
}

function send(batch: AnalyticsBatch, useBeacon: boolean) {
    const body = JSON.stringify(batch);
    // A string body is sent as text/plain, which the endpoint parses explicitly.
    if (
        useBeacon &&
        typeof navigator.sendBeacon === 'function' &&
        navigator.sendBeacon(ANALYTICS_ENDPOINT, body)
    ) {
        return;
    }
    fetch(ANALYTICS_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
        credentials: 'same-origin',
    }).catch(() => undefined); // failed flushes are dropped, never retried
}

export function flushAnalyticsQueue({ useBeacon = false }: { useBeacon?: boolean } = {}) {
    try {
        let batch = takeBatch();
        while (batch) {
            send(batch, useBeacon);
            batch = takeBatch();
        }
    } catch {
        // Capture never throws into the app.
    }
}

const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') flushAnalyticsQueue({ useBeacon: true });
};
const onPageHide = () => flushAnalyticsQueue({ useBeacon: true });

export function startAnalyticsQueue() {
    if (enabled) return;
    enabled = true;
    timer = setInterval(() => flushAnalyticsQueue(), FLUSH_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
}

export function stopAnalyticsQueue() {
    if (!enabled) return;
    flushAnalyticsQueue();
    enabled = false;
    if (timer) clearInterval(timer);
    timer = null;
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', onPageHide);
}

export function isAnalyticsEnabled() {
    return enabled;
}

export function enqueueAnalyticsEvent(event: AnalyticsEvent) {
    if (!enabled) return;
    try {
        if (queue.length >= MAX_QUEUE_SIZE) queue.shift(); // drop the oldest
        queue.push(event);
        if (queue.length >= FLUSH_SIZE) flushAnalyticsQueue();
    } catch {
        // Capture never throws into the app.
    }
}

export function setCurrentRoute(route: { routePattern: string | null; path: string }) {
    currentRoute = route;
}

export function getCurrentRoute() {
    return currentRoute;
}

// Returns true when the path differs from the last recorded page view (and records it).
export function claimPageView(path: string): boolean {
    if (path === lastPageViewPath) return false;
    lastPageViewPath = path;
    return true;
}

// Test-only reset of the module state.
export function resetAnalyticsQueueForTests() {
    stopAnalyticsQueue();
    queue = [];
    lastPageViewPath = null;
    currentRoute = { routePattern: null, path: null };
}
